import { createGlobalStyle, css } from 'styled-components';

function columnGenerator(suffix) {
	let style = '';
	for (let i = 1; i <= 12; i++) {
		style += suffix ? `.col-${suffix}-${i}` : `.col-${i}`;
		style += `{ width: ${(100 / 12) * i}% } `;
	}

	return style;
}

const Typography = createGlobalStyle`${css`
	* {
		margin: 0;
		padding: 0;
		outline: none;
		box-sizing: border-box;
		-webkit-appearance: none;
		-webkit-text-size-adjust: 100%;
		-webkit-font-smoothing: antialiased;
		-webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important; //! Touch highlight color

		/* width */
		::-webkit-scrollbar {
			width: var(--sp1x);
			border-radius: var(--sp2x);
		}

		/* Track */
		::-webkit-scrollbar-track {
			background: transparent;
		}

		/* Handle */
		::-webkit-scrollbar-thumb {
			background: var(--secondaryColor4);
			border-radius: var(--sp2x);
		}

		/* Handle on hover */
		::-webkit-scrollbar-thumb:hover {
			background: var(--secondaryColor2);
		}
	}

	body {
		width: 100%;
		min-height: 100%;
		color: var(--primaryColor1);
		background: var(--secondaryColor1);
		line-height: var(--lineHeight);
		position: relative;
		font-family: var(--ping);
		overflow-x: hidden;
		overflow-y: ${(props) => (props.popupIsOpen ? 'hidden' : '')};

		main {
			position: relative;
			display: flex;
			flex-direction: column;
			min-height: 100%;
			z-index: 1;
			transition: margin-top 1s;

			&.dark {
				background: var(--secondaryColor2);
				animation: loading 1s infinite;
			}
		}

		#__next {
			min-height: 100%;
		}

		/* &::-webkit-scrollbar {
            display: none;
        } */

		&::-webkit-scrollbar-thumb {
			background-color: var(--borderColor);
		}

		&.hidden {
			overflow: hidden;
			max-height: 100vh;
		}
	}

	ul,
	fieldset {
		list-style: none;
		border: none;
	}

	.hide-scrollbar {
		&::-webkit-scrollbar {
			display: none;
		}
	}

	input[type='search']::-webkit-search-decoration,
	input[type='search']::-webkit-search-cancel-button,
	input[type='search']::-webkit-search-results-button,
	input[type='search']::-webkit-search-results-decoration {
		-webkit-appearance: none;
	}

	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	input[type='number'] {
		-moz-appearance: textfield;
	}

	input:-internal-autofill-selected {
		background-color: transparent !important;
		box-shadow: inset 1000px 1000px #fff;
	}

	input {
		outline: none;
	}

	input:-webkit-autofill {
		-webkit-text-fill-color: var(--textColor) !important;
		box-shadow: inset 0 0 0 50px var(--textColor) !important;
	}

	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	textarea:-webkit-autofill,
	textarea:-webkit-autofill:hover,
	textarea:-webkit-autofill:focus,
	select:-webkit-autofill,
	select:-webkit-autofill:hover,
	select:-webkit-autofill:focus {
		border: 1px solid black;
		-webkit-text-fill-color: black;
		color: white;
		background-color: transparent;
		box-shadow: 0 0 0 1000px transparent inset;
		transition: background-color 5000s ease-in-out 0s;
	}

	//! Change the transparent to any color
	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	input:-webkit-autofill:active {
		box-shadow: 0 0 0 30px transparent inset !important;
	}

	@keyframes autofill {
		to {
			background: transparent;
		}
	}

	a {
		text-decoration: none;
		color: var(--black);
		-webkit-tap-highlight-color: transparent;
	}

	.h1,
	h1 {
		font-size: var(--h1);
		line-height: var(--lineHeightS);
		font-weight: unset;

		&.h1L {
			--h1: var(--h1L);
		}
	}

	.h2,
	h2 {
		font-size: var(--h2);
		line-height: var(--lineHeightS);
		font-weight: unset;
	}

	.h3,
	h3 {
		font-size: var(--h3);
		line-height: var(--lineHeightS);
		font-weight: unset;
	}

	.h4,
	h4 {
		font-size: var(--h4);
		line-height: var(--lineHeightS);
		font-weight: unset;
	}

	.h5,
	h5 {
		font-size: var(--h5);
		line-height: var(--lineHeightS);
		font-weight: unset;
	}

	.h6,
	h6 {
		font-size: var(--h6);
		line-height: var(--lineHeight);
		font-weight: unset;
	}

	.p,
	p {
		font-size: var(--p);
		line-height: var(--lineHeight);
		font-weight: unset;

		&.pS {
			--p: var(--pS);
		}

		&.pXS {
			--p: var(--pXS);
		}

		&.pXXS {
			--p: var(--pXXS);
		}
	}

	//! Row
	.row {
		display: flex;
		flex-wrap: wrap;
		margin: 0 calc(var(--colPadding) * -1);

		> * {
			width: 100%;
			padding: 0 var(--colPadding);
		}

		.col {
			flex: 1 1;
		}

		${columnGenerator()};

		@media only screen and (max-width: ${({ theme }) => theme.mediaQuery.DesktopSizeXLMin}) {
			${columnGenerator('dl')};
		}

		@media only screen and (max-width: ${({ theme }) => theme.mediaQuery.DesktopSizeLMin}) {
			${columnGenerator('dm')};
		}

		@media only screen and (max-width: ${({ theme }) => theme.mediaQuery.DesktopSizeMMin}) {
			${columnGenerator('ds')};
		}

		@media only screen and (max-width: ${({ theme }) => theme.mediaQuery.DesktopSizeSMin}) {
			${columnGenerator('dxs')};
		}

		@media only screen and (max-width: ${({ theme }) => theme.mediaQuery.DesktopSizeXSMin}) {
			${columnGenerator('t')};
		}

		@media only screen and (max-width: ${({ theme }) => theme.mediaQuery.TabletSizeMin}) {
			${columnGenerator('ts')};
		}

		@media only screen and (max-width: ${({ theme }) => theme.mediaQuery.TabletSizeSMin}) {
			${columnGenerator('m')};
		}
	}

	@keyframes loading {
		0%,
		100% {
			opacity: 0.7;
		}

		50% {
			opacity: 0.5;
		}
	}
`}`;

export default Typography;
